import { useEffect, useState } from "react";
import {
  generateResourceSuggestions,
  getGeneratedResourceSuggestionsFromCache,
  getPricingCalculatorDetails,
  getPricingDealRMODetails,
} from "../../services/Apis";
import {
  Button,
  Card,
  Col,
  ConfigProvider,
  Input,
  message,
  Modal,
  Popover,
  Row,
  Select,
  Skeleton,
  Table,
  Tag,
  Tooltip,
  Upload,
  UploadFile,
  UploadProps,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  EllipsisOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import StarIcon from "../../styles/ai-technology.png";
import type { UploadChangeParam } from "antd/es/upload";
import { sowParser } from "../../services/agentApis";
import { start } from "repl";
import OpenAirBookings from "./OpenAirBookings";
const { Dragger } = Upload;
const { TextArea } = Input;

export default function DealResourceRequestInfo({
  dealData,
}: {
  dealData: any;
}) {
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [dealRMODetails, setDealRMODetails] = useState<any>({});
  const [defaultResourcesData, setDefaultResourcesData] = useState<any>([]);

  useEffect(() => {
    setPageLoading(true);
    getPricingDealRMODetails(dealData.deal_id, dealData.version_id)
      .then((resp) => {
        setPageLoading(false);
        const errorMsg = (resp as any).error ?? (resp as any).data?.error;
        if (errorMsg) {
          message.error(errorMsg);
          return;
        }
        if ("data" in resp) {
          setDealRMODetails(resp.data);
        }
      })
      .catch((error) => {
        setPageLoading(false);
        message.error("Failed to Get Resource Request Data");
      });
    getPricingCalculatorDetails(
      dealData.deal_id,
      dealData.base_price_version_id
    )
      .then((resp) => {
        const errorMsg = (resp as any).error ?? (resp as any).data?.error;
        if (errorMsg) {
          message.error(errorMsg);
          return;
        }
        setDefaultResourcesData(resp);
      })
      .catch((error) => {
        message.error("Failed to Get Default Resource Request Data");
      });
  }, [dealData?.deal_id, dealData?.version_id, dealData?.base_price_version_id]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      {pageLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          <DealInfo dealData={dealRMODetails} />
          <ResourcesTable
            dealData={dealRMODetails}
            defaultResourcesData={defaultResourcesData}
          />
        </>
      )}
      <OpenAirBookings dealData={dealData} />
    </div>
  );
}

const resourceRequirementId = (resource: any): string =>
  String(resource.resource_requirement_id ?? resource.resource_id);

const getDefaultSkills = (dealData: any) => {
  return dealData?.resources?.reduce((acc: any, resource: any) => {
    const skills = resource.comment
      ? resource.comment
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean)
      : [];
    acc[resourceRequirementId(resource)] = skills;
    return acc;
  }, {});
};

const ResourcesTable = ({
  dealData,
  defaultResourcesData = [],
}: {
  dealData: any;
  defaultResourcesData: any;
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<any[]>([]);
  const [skillsData, setSkillsData] = useState<Record<string, string[]>>(
    getDefaultSkills(dealData)
  );
  const [additionalRegions, setAdditionalRegions] = useState<
    Record<string, any>
  >({});
  const [additionalFunctions, setAdditionalFunctions] = useState<
    Record<string, any>
  >({});
  const [certificatesData, setCertificatesData] = useState<
    Record<string, string[]>
  >({});
  const [jobDescriptionData, setJobDescriptionData] = useState<
    Record<string, string>
  >({});
  const [showGeneratedResults, setShowGeneratedResults] =
    useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [generatedData, setGeneratedData] = useState<any>({});
  const [dataLoading, setDataLoading] = useState(false);
  const [showingAlreadyGenerated, setShowingAlreadyGenerated] = useState(false);
  const [sowUploadModalOpen, setSowUploadModalOpen] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [selectedResources, setSelectedResources] = useState<any>([]);

  useEffect(() => {
    if (dealData && dealData.deal_id) {
      setDataLoading(true);
      getGeneratedResourceSuggestionsFromCache(dealData.deal_id)
        .then((resp) => {
          setDataLoading(false);
          if (resp.error_status == 404) {
            return;
          }
          if (resp.error || resp.error_status) {
            message.error(resp.error);
            return;
          }
          // if(resp.detail == '')
          setGeneratedData(resp.response_json.results);
          setSkillsData({
            ...resp.request_json.reduce(
              (
                acc: { [x: string]: any },
                resource: {
                  resource_request_id: string | number;
                  skills: never[];
                }
              ) => {
                acc[resource.resource_request_id] = Array.from(
                  new Set([
                    // ...(skillsData[resource.resource_request_id] || []),
                    ...(resource.skills || []),
                  ])
                );
                return acc;
              },
              {}
            ),
          });
          setCertificatesData(
            resp.request_json.reduce(
              (
                acc: { [x: string]: any },
                resource: {
                  resource_request_id: string | number;
                  optional_certifications: never[];
                }
              ) => {
                acc[resource.resource_request_id] =
                  resource.optional_certifications || [];
                return acc;
              },
              {}
            )
          );
          setJobDescriptionData(
            resp.request_json.reduce(
              (
                acc: { [x: string]: any },
                resource: {
                  resource_request_id: string | number;
                  job_description: string | undefined;
                }
              ) => {
                acc[resource.resource_request_id] =
                  resource.job_description || "";
                return acc;
              },
              {}
            )
          );
          setAdditionalRegions(
            resp.request_json.reduce(
              (
                acc: { [x: string]: any },
                resource: {
                  resource_request_id: string | number;
                  regions: never[];
                }
              ) => {
                acc[resource.resource_request_id] =
                  resource.regions?.slice(1, resource.regions.length) || [];
                return acc;
              },
              {}
            )
          );
          setAdditionalFunctions(
            resp.request_json.reduce(
              (
                acc: { [x: string]: any },
                resource: {
                  resource_request_id: string | number;
                  function_ids: never[];
                  alternate_service_lines: string[];
                }
              ) => {
                acc[resource.resource_request_id] =
                  resource.function_ids
                    ?.slice(1, resource.function_ids.length)
                    ?.map((fn: number, idx: number) => {
                      return `${resource.alternate_service_lines[idx]}::${fn}`;
                    }) || [];
                return acc;
              },
              {}
            )
          );
          setExpandedRowKeys(
            dealData.resources.map((resource: any) => resourceRequirementId(resource))
          );
          setShowingAlreadyGenerated(true);
          setShowGeneratedResults(true);
        })
        .catch((error) => {
          setDataLoading(false);
          message.error("Failed to Get Resource Request Data");
        });
    }
  }, [dealData?.deal_id]);

  const RenderSkills = ({ id }: { id: string | number }) => {
    const [skillInput, setSkillInput] = useState<string>("");
    const [addSkillVisible, setAddSkillVisible] = useState<boolean>(false);

    const handleAddSkills = () => {
      if (skillInput) {
        if (skillInput.includes(",")) {
          setSkillsData({
            ...skillsData,
            [id]: [
              ...(skillsData[id] || []),
              ...skillInput.split(",").map((s) => s.trim()),
            ],
          });
        } else {
          setSkillsData({
            ...skillsData,
            [id]: [...(skillsData[id] || []), skillInput],
          });
        }
        setSkillInput("");
      }
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          maxWidth: "220px",
        }}
      >
        {addSkillVisible && (
          <div style={{ display: "flex", gap: "2px", alignItems: "center" }}>
            <Input
              allowClear
              placeholder="separate by (,) for multiple skills"
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              onPressEnter={() => handleAddSkills()}
              size="small"
            />
            <Tag
              onClick={() => handleAddSkills()}
              style={{ cursor: "pointer", marginRight: 0 }}
              bordered={false}
            >
              <CheckOutlined />
            </Tag>
            <Tag
              onClick={() => setAddSkillVisible(false)}
              style={{ cursor: "pointer", marginRight: 0 }}
              bordered={false}
            >
              <CloseOutlined />{" "}
            </Tag>
          </div>
        )}
        {/* {<>{console.log(skillsData, "llll")}</>} */}
        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
          {skillsData[id]?.map((skill) => (
            <Tag
              key={skill}
              color="blue"
              closable
              onClose={() =>
                setSkillsData({
                  ...skillsData,
                  [id]: skillsData[id].filter((s) => s !== skill),
                })
              }
              // onClick={() => {
              //   setSkillInput(skill);
              //   setSkillsData({
              //     ...skillsData,
              //     [id]: skillsData[id].filter((s) => s !== skill),
              //   });
              // }}
              style={{ marginRight: 0 }}
            >
              {skill}
            </Tag>
          ))}

          {!addSkillVisible && (
            <Tooltip title="Add Skill(s)">
              <Tag
                onClick={() => setAddSkillVisible(true)}
                style={{ cursor: "pointer" }}
                bordered={false}
              >
                <PlusOutlined />
              </Tag>
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  const RenderCertificates = ({ id }: { id: string | number }) => {
    const [certificateInput, setCertificateInput] = useState<string>("");
    const [addCertificateVisible, setAddCertificateVisible] =
      useState<boolean>(false);

    const handleAddCertificates = () => {
      if (certificateInput) {
        setCertificatesData({
          ...certificatesData,
          [id]: [...(certificatesData[id] || []), certificateInput],
        });
        setCertificateInput("");
      }
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          maxWidth: "360px",
        }}
      >
        {addCertificateVisible && (
          <div style={{ display: "flex", gap: "2px", alignItems: "center" }}>
            <Input
              allowClear
              placeholder="Add a certificate"
              value={certificateInput}
              onChange={(e) => setCertificateInput(e.target.value)}
              onPressEnter={() => handleAddCertificates()}
              size="small"
            />
            <Tag
              onClick={() => handleAddCertificates()}
              style={{ cursor: "pointer", marginRight: 0 }}
              bordered={false}
            >
              <CheckOutlined />
            </Tag>
            <Tag
              onClick={() => setAddCertificateVisible(false)}
              style={{ cursor: "pointer", marginRight: 0 }}
              bordered={false}
            >
              <CloseOutlined />{" "}
            </Tag>
          </div>
        )}
        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
          {certificatesData[id]?.map((certificate) => (
            <Tag
              key={certificate}
              color="orange"
              closable
              onClose={() =>
                setCertificatesData({
                  ...certificatesData,
                  [id]: certificatesData[id].filter((c) => c !== certificate),
                })
              }
              // onClick={() => {
              //   setCertificateInput(certificate);
              //   setCertificatesData({
              //     ...certificatesData,
              //     [id]: certificatesData[id].filter((c) => c !== certificate),
              //   });
              // }}
              style={{ marginRight: 0, textWrap: "wrap" }}
            >
              {certificate}
            </Tag>
          ))}
          {!addCertificateVisible && (
            <Tooltip title="Add Certificate">
              <Tag
                onClick={() => setAddCertificateVisible(true)}
                style={{ cursor: "pointer" }}
                bordered={false}
              >
                <PlusOutlined />
              </Tag>
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  const RenderSelectionFilters = ({
    id,
    resourceData,
  }: {
    id: string | number;
    resourceData: any;
  }) => {
    const [filterOpen, setFilterOpen] = useState<boolean>(false);
    const regionFilters: any =
      defaultResourcesData?.filter(
        (r: any) => r.region != resourceData.region
      ) || [];

    const [regionInput, setRegionInput] = useState<any>(
      additionalRegions[id] || []
    );
    const [functionInput, setFunctionInput] = useState<any>(
      additionalFunctions[id] || []
    );

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          maxWidth: "220px",
          justifyContent: "center",
        }}
      >
        {!filterOpen &&
          regionInput.map((r: any) => {
            return (
              <Tag style={{ marginRight: 0 }} color="purple" key={r}>
                {r}
              </Tag>
            );
          })}
        {!filterOpen &&
          functionInput.map((r: any) => {
            return (
              <Tag style={{ marginRight: 0 }} color="magenta" key={r}>
                {r.split("::")[0]}
              </Tag>
            );
          })}
        <Popover
          content={
            regionFilters?.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  minWidth: "240px",
                  marginBottom: "12px",
                }}
              >
                <div>Select Alternate Region</div>
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  options={Array.from(
                    new Set(regionFilters.map((obj: any) => obj.region))
                  ).map((r: any) => ({ label: r, value: r }))}
                  onChange={(value) => {
                    setRegionInput(value);
                  }}
                  value={regionInput}
                />
                <div>Select Alternate Function</div>
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  options={Array.from(
                    new Set(
                      defaultResourcesData
                        .filter((r: any) => {
                          return (
                            regionInput.includes(r.region) ||
                            r.region == resourceData.region
                          );
                        })
                        .map(
                          (obj: any) =>
                            `${obj.service_line}, ${obj.function}::${obj.function_id}`
                        )
                    )
                  ).map((f: any) => ({ label: f.split("::")[0], value: f }))}
                  onChange={(value) => {
                    setFunctionInput(value);
                  }}
                  value={functionInput}
                />
              </div>
            ) : (
              <>No Available Additional Regions/Functions </>
            )
          }
          title={
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "flex-end",
              }}
            >
              <Button
                icon={<CheckOutlined />}
                type="link"
                onClick={() => {
                  setAdditionalRegions({
                    ...additionalRegions,
                    [id]: regionInput,
                  });
                  setAdditionalFunctions({
                    ...additionalFunctions,
                    [id]: functionInput,
                  });
                  setFilterOpen(false);
                }}
              >
                Done
              </Button>
            </div>
          }
          trigger="click"
          open={filterOpen}
          // onOpenChange={handleOpenChange}
        >
          <div>
            {!filterOpen ? (
              <SettingOutlined
                style={{ cursor: "pointer" }}
                onClick={() => setFilterOpen(!filterOpen)}
              />
            ) : (
              <>
                <CloseOutlined
                  style={{ cursor: "pointer" }}
                  onClick={() => setFilterOpen(!filterOpen)}
                />
              </>
            )}
          </div>
        </Popover>
      </div>
    );
  };

  const columns: any = [
    {
      title: "Resource",
      dataIndex: "resource_type",
      key: "resource_type",
      render: (_: any, record: any) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <div style={{ fontSize: "16px" }}>{record.career_level}</div>
          <div style={{ fontSize: "12px" }}>
            {record.service_line}, {record.function} | {record.resource_type} -{" "}
            {record.region}
          </div>
        </div>
      ),
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      render: (text?: string) => (
        <span>{text ? text.substring(0, 10) : ""}</span>
      ),
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      render: (text?: string) => (
        <span>{text ? text.substring(0, 10) : ""}</span>
      ),
    },
    // {
    //   title: "Duration",
    //   dataIndex: "duration",
    //   key: "duration",
    // },
    {
      title: (
        <div>
          % Allocation{" "}
          <Tooltip title="Hover to view monthwise allocation">
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      ),
      dataIndex: "allocation",
      key: "allocation",
      align: "center",
      render: (_: any, record: any) => (
        <Popover
          content={
            <Table
              columns={[
                {
                  title: "month",
                  dataIndex: "month",
                  key: "month",
                  align: "center",
                  render: (_: any, record: any) => (
                    <span>{record.month.substring(0, 8)}</span>
                  ),
                },
                {
                  title: "allocation (%)",
                  dataIndex: "allocation",
                  key: "allocation",
                  align: "center",
                },
              ]}
              dataSource={
                Object.keys(record.monthly_wise_allocation).map((month) => ({
                  month,
                  allocation: record.monthly_wise_allocation[month],
                })) || []
              }
              pagination={false}
            />
          }
          title="Monthwise Allocation"
        >
          <div> {record.allocation} %</div>
        </Popover>
      ),
    },
    {
      title: (
        <div>
          Skills{" "}
          <Tooltip title="Add Multiple Skills by separating with comma">
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      ),
      dataIndex: "skills",
      key: "skills",
      render: (_: any, record: any) => (
        <RenderSkills id={resourceRequirementId(record)} key={resourceRequirementId(record)} />
      ),
      // width: 220,
      // fixed: "right",
    },
    {
      title: "Certifications",
      dataIndex: "certifications",
      key: "certifications",
      render: (_: any, record: any) => (
        <RenderCertificates id={resourceRequirementId(record)} key={resourceRequirementId(record)} />
      ),
      // width: 260,
      // fixed: "right",
    },
    {
      title: "Job Description",
      dataIndex: "job_description",
      key: "job_description",
      render: (_: any, record: any) => (
        <TextArea
          key={resourceRequirementId(record)}
          onChange={(e) =>
            setJobDescriptionData({
              ...jobDescriptionData,
              [resourceRequirementId(record)]: e.target.value,
            })
          }
          value={jobDescriptionData[resourceRequirementId(record)] || ""}
        />
      ),
      width: 260,
      // fixed: "right",
    },
    {
      title: (
        <div>
          Selection Filters{" "}
          <Tooltip title="Select alternate region/function to get more results">
            <QuestionCircleOutlined />
          </Tooltip>
        </div>
      ),
      dataIndex: "selection_filters",
      key: "selection_filters",
      render: (_: any, record: any) => (
        <RenderSelectionFilters
          id={resourceRequirementId(record)}
          key={resourceRequirementId(record)}
          resourceData={record}
        />
      ),
      align: "center",
    },
  ];

  const handleGenerateResourceSuggestions = async () => {
    setGenerating(true);
    try {
      let requestPayload = dealData.resources.map((r: any) => ({
        resource_request_id: resourceRequirementId(r),
        regions: [r.region, ...(additionalRegions[resourceRequirementId(r)] || [])],
        service_line: `${r.service_line}, ${r.function}`,
        function_ids: Array.from(
          new Set([
            r.function_id,
            ...(additionalFunctions[resourceRequirementId(r)] || []).map((f: any) =>
              Number(f.split("::")[1])
            ),
          ])
        ),
        alternate_service_lines: Array.from(
          new Set([
            ...(additionalFunctions[resourceRequirementId(r)] || []).map(
              (f: any) => f.split("::")[0]
            ),
          ])
        ),
        level: r.career_level ?? "",
        start_date: r.start_date?.substring(0, 10),
        end_date: r.end_date?.substring(0, 10),
        required_certifications: [],
        optional_certifications: certificatesData[resourceRequirementId(r)] || [],
        skills: skillsData[resourceRequirementId(r)]?.map((sk) => sk.toLowerCase()) || [],
        monthly_requirements: Object.keys(r.monthly_wise_allocation).map(
          (month: any) => ({
            month: month,
            required_percentage: r.monthly_wise_allocation[month],
          })
        ),
        job_description: jobDescriptionData[resourceRequirementId(r)] || "",
      }));
      if (selectedResources && selectedResources.length > 0) {
        requestPayload = requestPayload.filter((r: any) =>
          selectedResources.includes(r.resource_request_id.toString())
        );
      }
      const suggestions = await generateResourceSuggestions(
        dealData.deal_id,
        requestPayload
      );
      if (suggestions.error || suggestions.error_status) {
        message.error(
          `Failed to Generate Resource Suggestions! ${suggestions.error_status}`
        );
        return;
      }
      setGeneratedData({ ...generatedData, ...(suggestions?.results || {}) });
      setExpandedRowKeys([
        ...expandedRowKeys,
        ...Object.keys(suggestions?.results || {}),
      ]);
      setShowGeneratedResults(true);
      setShowingAlreadyGenerated(false);
    } catch (error) {
      console.error("Error generating resource suggestions:", error);
    } finally {
      setGenerating(false);
    }
  };

  const parseSowFile = () => {
    if (fileList?.length < 1) {
      message.error("Please upload a file before submitting");
      return;
    }
    setIsFileUploading(true);
    const formData = new FormData();
    formData.append(
      "body",
      JSON.stringify({
        project_id: dealData.deal_id,
        project_name: dealData.deal_name,
        requirements: dealData.resources.map((r: any) => ({
          ...r,
          resource_id: resourceRequirementId(r),
          career_level_name: r.career_level,
          function_code: r.function,
          region_code: r.region,
          resource_type_code: r.resource_type,
          service_line_code: r.service_line,
          start_date: r.start_date?.substring(0, 10),
          end_date: r.end_date?.substring(0, 10),
        })),
      })
    );
    if (fileList[0].originFileObj instanceof Blob) {
      formData.append("file", fileList[0].originFileObj);
    } else {
      message.error("Uploaded file is not valid.");
      setIsFileUploading(false);
      return;
    }
    sowParser(formData).then((resp) => {
      setIsFileUploading(false);
      if (resp.error || resp.error_status) {
        message.error(`Failed to Parse SOW File! ${resp.error}`);
        return;
      }

      const updatedSkillsData: Record<string, string[]> = {}; // Start with existing state
      const certificationsData: Record<string, string[]> = {}; // Start with existing state
      const updatedJobDescriptionData: Record<string, string> = {};

      resp?.requirements?.forEach((requirement: any) => {
        // const resourceId = requirement.resource_id;
        // const newSkills = requirement.skills_required;

        // // Check if the resourceId already exists in the skillsData state
        // if (updatedSkillsData[resourceId]) {
        //   // Union the old skills with the new ones, ensuring uniqueness
        //   updatedSkillsData[resourceId] = Array.from(
        //     new Set([...updatedSkillsData[resourceId], ...newSkills])
        //   );
        // } else {
        //   // If the resourceId does not exist, just add the new skills
        //   updatedSkillsData[resourceId] = newSkills;
        // }
        updatedSkillsData[requirement.resource_id] =
          requirement.skills_required;
        certificationsData[requirement.resource_id] =
          requirement.certificates_required;
        updatedJobDescriptionData[requirement.resource_id] =
          requirement.job_description;
      });

      // Update the state with the new skills data
      setSkillsData(updatedSkillsData);
      setCertificatesData(certificationsData);
      setJobDescriptionData(updatedJobDescriptionData);
      message.success("SOW Parsed Successfully");
      setSowUploadModalOpen(false);
      setFileList([]);
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {showingAlreadyGenerated && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Tag icon={<ExclamationCircleOutlined />} color="warning">
            <strong>Note:</strong> Previously Generated Suggestions Are Shown
            Below
          </Tag>
        </div>
      )}
      <Card
        title="Resources"
        extra={
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Button
              onClick={() => setSowUploadModalOpen(true)}
              variant="link"
              type="link"
              style={{ color: "#6a48f3" }}
              size="large"
            >
              <img src={StarIcon} alt="Star Icon" width={16} height={16} />
              <div style={{ paddingTop: "3px" }}>Autofill using SOW</div>
            </Button>
            <Button
              onClick={() => {
                if(!selectedResources || selectedResources.length < 1){
                  message.warning("Please select at least one resource to suggest");
                  return;
                }
                handleGenerateResourceSuggestions();
              }}
              variant="link"
              type="link"
              style={{ color: "#6a48f3" }}
              size="large"
              // disabled={!selectedResources || selectedResources.length < 1}
            >
              <img src={StarIcon} alt="Star Icon" width={16} height={16} />
              <div style={{ paddingTop: "3px" }}>Suggest Resources</div>
            </Button>
          </div>
        }
      >
        {/* {<>{console.log(skillsData, certificatesData, additionalRegions, additionalFunctions)}</>} */}
        <Table
          dataSource={
            dealData.resources
              ? [...dealData.resources].sort((a, b) => {
                  if (a.key_id < b.key_id) return -1;
                  if (a.key_id > b.key_id) return 1;
                  return 0;
                })
              : []
          }
          columns={columns}
          rowKey={(record) => resourceRequirementId(record)}
          expandable={{
            expandedRowRender: (record, index, indent, expanded) => (
              // <div style={{ overflowX: "scroll", maxWidth: "95%" }}>
                <GeneratedResultsTable
                  generatedData={generatedData[resourceRequirementId(record)] ?? []}
                  // generatedData={generatedData[Math.random() < 0.5 ? "1" : "2"]}
                />
              // {/* </div> */}
            ),
            rowExpandable: (record) =>
              showGeneratedResults && resourceRequirementId(record) !== "" && generatedData[resourceRequirementId(record)],
            expandedRowKeys: expandedRowKeys,
            onExpand: (expanded, record) => {
              if (expanded) {
                setExpandedRowKeys([...expandedRowKeys, resourceRequirementId(record)]);
              } else {
                setExpandedRowKeys(
                  expandedRowKeys.filter((k) => k != resourceRequirementId(record))
                );
              }
            },
          }}
          rowSelection={{
            onChange: (selectedRowKeys, selectedRows) => {
              setSelectedResources(selectedRowKeys);
            },
          }}
          scroll={{ x: 1201 }}
          loading={generating || dataLoading}
        />
      </Card>
      {sowUploadModalOpen && (
        <Modal
          open={sowUploadModalOpen}
          onCancel={() => setSowUploadModalOpen(false)}
          closeIcon={null}
          footer={null}
        >
          <Dragger
            accept=".pdf"
            multiple={false}
            showUploadList={true}
            beforeUpload={(file) => {
              return false;
            }}
            fileList={fileList}
            customRequest={(options) => {
              return true;
            }}
            onRemove={() => setFileList([])}
            onChange={({ fileList: newFileList }: UploadChangeParam) =>
              setFileList(newFileList)
            }
          >
            <p className="ant-upload-drag-icon">
              <CloudUploadOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">.pdf</p>
          </Dragger>
          <Button
            block
            style={{ marginTop: 8 }}
            variant="filled"
            color={fileList?.length > 0 ? "primary" : "default"}
            onClick={() => parseSowFile()}
            loading={isFileUploading}
          >
            Submit
          </Button>
        </Modal>
      )}
    </div>
  );
};

const RenderMatchingSkills = ({ record }: { record: any }) => {
  const [showAll, setShowAll] = useState<boolean>(false);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        // maxWidth: "400px",
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "4px",
          flexWrap: "wrap",
          // maxWidth: "400px",
          flexDirection: "column",
        }}
      >
        {typeof record.matching_skills === "string"
          ? record.matching_skills
              .split(";")
              .map((skill: string, index: number) => (
                <Tag key={index} color="blue" style={{ marginRight: 0 }}>
                  {skill}
                </Tag>
              ))
          : record.matching_skills?.map((skill: string, index: number) => (
              <Tag key={index} color="blue" style={{ marginRight: 0 }}>
                {skill}
              </Tag>
            ))}
        {record.matching_skills && record.matching_skills.length < 1 && (
          <Tag bordered={false}>-</Tag>
        )}
      </div>
      {record.skills && record.skills.length > 0 && (
        <div>
          {showAll ? (
            <>
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  // maxWidth: "400px",
                  flexWrap: "wrap",
                  flexDirection: "column",
                }}
              >
                {typeof record.skills === "string"
                  ? record.skills
                      .split(";")
                      .map((skill: string, index: number) => (
                        <Tag
                          key={index}
                          color="cyan"
                          style={{ marginRight: 0 }}
                        >
                          {skill}
                        </Tag>
                      ))
                  : record.skills?.map((skill: string, index: number) => (
                      <Tag key={index} color="cyan" style={{ marginRight: 0 }}>
                        {skill}
                      </Tag>
                    ))}

                <Tag
                  onClick={() => setShowAll(false)}
                  bordered={false}
                  style={{ cursor: "pointer" }}
                >
                  Show Less...
                </Tag>
              </div>
            </>
          ) : (
            <div style={{ alignSelf: "flex-end" }}>
              <Tooltip title="Displaying Matched Skills. Click to view all skills">
                <Tag
                  onClick={() => setShowAll(true)}
                  bordered={false}
                  style={{ cursor: "pointer" }}
                  color="blue"
                >
                  <EllipsisOutlined />
                </Tag>
              </Tooltip>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RenderMatchingCertifications = ({ record }: { record: any }) => {
  const [showAll, setShowAll] = useState<boolean>(false);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        // maxWidth: "360px",
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "4px",
          flexWrap: "wrap",
          // maxWidth: "400px",
          flexDirection: "column",
        }}
      >
        {record.matching_certifications?.map(
          (certification: string, index: number) => (
            <Tag key={index} color="orange" style={{ marginRight: 0 }}>
              {certification}
            </Tag>
          )
        )}
        {record.matching_certifications &&
          record.matching_certifications.length < 1 && (
            <Tag bordered={false}>-</Tag>
          )}
      </div>
      {record.certifications && record.certifications.length > 0 && (
        <div>
          {showAll ? (
            <div
              style={{
                display: "flex",
                gap: "4px",
                // maxWidth: "360px",
                flexWrap: "wrap",
                flexDirection: "column",
              }}
            >
              {record.certifications?.map(
                (certification: string, index: number) => (
                  <Tag key={index} color="gold" style={{ marginRight: 0 }}>
                    {certification}
                  </Tag>
                )
              )}
              <Tag
                onClick={() => setShowAll(false)}
                bordered={false}
                style={{ cursor: "pointer" }}
              >
                Show Less...
              </Tag>
            </div>
          ) : (
            <div style={{ alignSelf: "flex-end" }}>
              <Tooltip title="Displaying Matched Certifications. Click to view all certifications">
                <Tag
                  onClick={() => setShowAll(true)}
                  bordered={false}
                  color="orange"
                  style={{ cursor: "pointer" }}
                >
                  <EllipsisOutlined />
                </Tag>
              </Tooltip>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const GeneratedResultsTable = ({ generatedData }: { generatedData: any }) => {
  const columns: any = [
    {
      title: "#",
      dataIndex: "rank",
      key: "rank",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_: any, record: any) => (
        <Tooltip title={record.email}>{record.name}</Tooltip>
      ),
    },
    {
      title: "Info",
      dataIndex: "info",
      key: "info",
      render: (_: any, record: any) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <div style={{ fontSize: "16px" }}>{record.level}</div>
          <div style={{ fontSize: "12px" }}>
            {record.service_line}, {record.region}
          </div>
        </div>
      ),
    },
    {
      title: (
        <div>
          Available (%){" "}
          <Tooltip title="Hover to view booking details for other projects">
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      ),
      dataIndex: "free_available_percent",
      key: "free_available_percent",
      align: "center",
      render: (_: any, record: any) => (
        <Popover
          content={
            <Table
              columns={[
                {
                  title: "Project",
                  dataIndex: "project_name",
                  key: "project_name",
                },
                {
                  title: "Start Date",
                  dataIndex: "start_date",
                  key: "start_date",
                },
                {
                  title: "End Date",
                  dataIndex: "end_date",
                  key: "end_date",
                },
                {
                  title: "% Booked",
                  dataIndex: "booked_percent",
                  key: "booked_percent",
                  align: "center",
                },
              ]}
              dataSource={record.bookings_for_other_project_details}
              pagination={false}
            />
          }
          title="Booking Details for other projects"
        >
          <span>
            {record.free_available_percent
              ? record.free_available_percent
              : "-"}
          </span>
        </Popover>
      ),
    },
    {
      title: "Skills",
      dataIndex: "matching_skills",
      key: "matching_skills",
      render: (_: any, record: any) => {
        return <RenderMatchingSkills record={record} />;
      },
    },
    {
      title: "Certifications",
      dataIndex: "matching_certifications",
      key: "matching_certifications",
      render: (_: any, record: any) => {
        return <RenderMatchingCertifications record={record} />;
      },
    },
    {
      title: "Score (/10)",
      dataIndex: "score",
      key: "score",
      render: (score: any) => {
        return (
          <Tag
            color={
              score >= 9
                ? "green"
                : score >= 7
                ? "lime"
                : score >= 5
                ? "gold"
                : score >= 1
                ? "volcano"
                : "default"
            }
            bordered={false}
          >
            {score}
          </Tag>
        );
      },
      align: "center",
    },
    {
      title: "Rationale",
      dataIndex: "reason_for_rank",
      key: "reason_for_rank",
      render: (reason_for_rank: any) => {
        return (
          <Tooltip title={reason_for_rank}>
            <ExclamationCircleOutlined />
          </Tooltip>
        );
      },
      align: "center",
    },
  ];
  return (
    <ConfigProvider
      theme={{
        token: {
          colorBgContainer: "#f6f4ff91",
        },
      }}
    >
      <Card>
        <Table
          dataSource={generatedData}
          columns={columns}
          pagination={false}
          scroll={{ x: 1200 }}
        />
      </Card>
    </ConfigProvider>
  );
};

const DealInfo = ({ dealData }: { dealData: any }) => {
  return (
    <Card title="Deal Details">
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <b>Client Name:</b> {dealData?.company_name}
        </Col>
        <Col span={8}>
          <b>Deal Name:</b> {dealData?.deal_name}
        </Col>
        <Col span={8}>
          <b>Deal ID:</b> {dealData?.deal_id}
        </Col>
        <Col span={8}>
          <b>Deal Stage:</b> {dealData?.deal_stage_name}
        </Col>
        <Col span={8}>
          <b>Engagement Type:</b> {dealData?.engagement_type}
        </Col>

        <Col span={8}>
          <b>Expected Start Date:</b>{" "}
          {dealData?.project_start_date?.substring(0, 10)}
        </Col>
        <Col span={8}>
          <b>Duration:</b> {dealData?.duration_in_months}
        </Col>
        <Col span={8}>
          <b>Close Date:</b> {dealData?.project_close_date?.substring(0, 10)}
        </Col>
        <Col span={8}>
          <b>Deal Owner:</b> {dealData?.deal_owner_name}
        </Col>
      </Row>
    </Card>
  );
};
